import type { ApprovalItem, RegistryState } from '../../data/types';
import { money } from '../../lib/format';
import { LEDGER_LABELS } from '../../lib/labels';
import type { RegistryAction } from '../actions';
import {
  actorName,
  addApproval,
  appendLedger,
  fundCode,
  holderName,
  today,
  withAudit,
} from '../helpers';

type RequestAction = Extract<RegistryAction, { type: 'LEDGER_REVERSAL_REQUEST' }>;

export function isReversed(state: RegistryState, entryId: string): boolean {
  return state.ledger.some((entry) => entry.reversalOf === entryId);
}

export function requestLedgerReversal(state: RegistryState, action: RequestAction): RegistryState {
  const entry = state.ledger.find((item) => item.id === action.entryId);
  if (!entry || entry.type === 'ADJUSTMENT' || isReversed(state, entry.id)) return state;
  if (
    state.approvals.some(
      (item) => item.kind === 'ADJUSTMENT' && item.refId === entry.id && item.status === 'PENDING',
    )
  ) {
    return state;
  }
  const next = addApproval(state, {
    kind: 'ADJUSTMENT',
    refId: entry.id,
    fundId: entry.fundId,
    title: `Reverse ledger entry #${entry.seq}: ${LEDGER_LABELS[entry.type]}, ${holderName(state, entry.holderId)}`,
    summary: `${fundCode(state, entry.fundId)} · ${entry.units ? `${entry.units} units · ` : ''}${money(entry.amount, true)}. Reason: ${action.reason}`,
    amount: entry.amount,
    changes: [
      { field: 'Units', from: String(entry.units), to: String(-entry.units) },
      { field: 'Amount', from: money(entry.amount, true), to: `(${money(entry.amount, true)})` },
    ],
  });
  return withAudit(next, 'Ledger reversal requested', `Entry #${entry.seq}`, action.reason);
}

export function applyLedgerReversal(
  state: RegistryState,
  item: ApprovalItem,
  approve: boolean,
  approver: string,
): RegistryState {
  const entry = state.ledger.find((candidate) => candidate.id === item.refId);
  if (!approve || !entry || isReversed(state, entry.id)) return state;
  const reason = item.summary.split('Reason: ')[1] ?? 'Approved correction';
  return appendLedger(state, [
    {
      fundId: entry.fundId,
      classId: entry.classId,
      holderId: entry.holderId,
      type: 'ADJUSTMENT',
      effectiveDate: today,
      units: -entry.units,
      amount: entry.amount,
      unitPrice: entry.unitPrice,
      reference: `REV #${entry.seq}`,
      eventId: entry.eventId,
      memo: `Reversal of #${entry.seq} (${LEDGER_LABELS[entry.type].toLowerCase()}): ${reason}`,
      postedBy: item.requestedBy || actorName(state),
      approvedBy: approver,
      reversalOf: entry.id,
      reversedType: entry.type,
    },
  ]);
}
